function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Copyright (c) 2018 Mathis Zeiher
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */

/* globals window Promise HTMLElement console Symbol */
import { camelToKebapCase, kebapToCamelCase, deserializeValue, serializeValue } from './utils.js';
import { getClassProperties } from './classproperties.js';
import { getComponentProperties } from './componentproperties.js';
import { PROPERTY_STATE } from './propertystate.js';
import { getClassPropertyWatcher } from './classpropertywatcher.js';
import { getClassPropertyInterceptor } from './classpropertyinterceptors.js';
import { renderComponent } from './renderer/renderComponent.js';
import { COMPONENT_STATE } from './componentstate.js';
import warn from './warn.js';

/**
 * default comparator, compares identity
 *
 * @param oldValue - old value to compare
 * @param newValue - new value to compare
 */
const defaultComparator = (oldValue, newValue) => {
  return oldValue !== newValue;
};

const PolyfillPromise = window['__zone_symbol__Promise'] ? // eslint-disable-line
window['__zone_symbol__Promise'] : typeof Promise !== 'undefined' && Promise.name === 'ZoneAwarePromise' ? // eslint-disable-line
window['__zone_symbol__Promise'] : Promise; // eslint-disable-line

const [requestImmediate, clearImmediate] = (() => {
  const timeouts = [];
  const messageName = Math.random.toString();
  let nbr = 0; // Like setTimeout, but only takes a function argument.  There's
  // no time argument (always zero) and no arguments (you have to
  // use a closure).

  /**
   * requestImmediate function
   *
   * @param fn - callback which gets invoked
   */

  function requestImmediateFn(fn) {
    const id = nbr === Number.MAX_SAFE_INTEGER ? 0 : nbr++;
    timeouts.push({
      id,
      fn
    });
    window.postMessage({
      messageName,
      id
    }, '*');
    return id;
  }
  /**
   * handle message (requestImmediate solved through postMessage)
   *
   * @param event - message event
   */


  function handleMessage(event) {
    if (event.source === window && typeof event.data.messageName !== 'undefined' && event.data.messageName === messageName) {
      event.stopPropagation();
      let i = -1;
      timeouts.forEach((value, index) => {
        if (value.id === event.data.id) {
          i = index;
        }
      });

      if (i >= 0) {
        timeouts.splice(i, 1)[0].fn();
      }
    }
  }
  /**
   * clear immediate
   *
   * @param id - callback id
   */


  function clearImmediateFn(id) {
    let i = -1;
    timeouts.forEach((value, index) => {
      if (value.id === id) {
        i = index;
      }
    });

    if (i >= 0) {
      timeouts.splice(i, 1);
    }
  }

  window.addEventListener('message', handleMessage, true); // Add the one thing we want added to the window object.

  return [requestImmediateFn, clearImmediateFn];
})();
/**
 * interface for an indexable element
 */


/**
 * define the render strategy for the control
 */
export let RENDER_STRATEGY;

(function (RENDER_STRATEGY) {
  RENDER_STRATEGY[RENDER_STRATEGY["DEFAULT"] = 0] = "DEFAULT";
  RENDER_STRATEGY[RENDER_STRATEGY["LAZY"] = 1] = "LAZY";
  RENDER_STRATEGY[RENDER_STRATEGY["PIPELINE_EXPERIMENTAL"] = 2] = "PIPELINE_EXPERIMENTAL";
  RENDER_STRATEGY[RENDER_STRATEGY["IMMEDIATE"] = 3] = "IMMEDIATE";
})(RENDER_STRATEGY || (RENDER_STRATEGY = {}));

export const symbolPropertyState = Symbol('property_state');
export const symbolComponentState = Symbol('component_state');
/**
 * Base class for all custom elements
 */

export class CustomElement extends HTMLElement {
  static get is() {
    return getComponentProperties(this).tag; // memoize and dynamically get element name via instantiation if not defined
  }

  static _fromAttribute(name, oldValue, newValue, instance) {
    if (instance[symbolPropertyState] !== PROPERTY_STATE.REFLECTING) {
      const propertyName = kebapToCamelCase(name);
      const classProperty = getClassProperties(this).get(propertyName);

      if (instance[symbolComponentState] & 0b1000) {
        // warning for developers if they mutate state during render
        warn(`WARNING: You are trying to mutate the property "${propertyName}" in element ${instance.localName} in a render callback render()/component(Will|Did|First)Render(). This is not supported and will lead to an error in 4.0.0`);
      }

      oldValue = instance[propertyName];

      if (classProperty.converter) {
        if (classProperty.converter.fromAttribute) {
          newValue = classProperty.converter.fromAttribute(newValue, classProperty.type);
        } else {
          newValue = classProperty.converter(newValue, classProperty.type);
        }
      } else {
        newValue = deserializeValue(newValue, classProperty.type);
      }

      if ((classProperty.comparator || defaultComparator)(oldValue, newValue)) {
        instance[symbolPropertyState] = PROPERTY_STATE.UPDATE_FROM_ATTRIBUTE;

        this._fromProperty(propertyName, oldValue, newValue, instance);
      }
    }
  }

  static _fromProperty(propertyKey, oldValue, newValue, instance) {
    instance._updatingProperty = propertyKey;

    if (instance[symbolComponentState] & 0b1000) {
      // warning for developers if they mutate state during render (on first render it's allowed due to compatibiliy reasons)
      warn(`WARNING: You are trying to mutate the property "${propertyKey.toString()}" in element ${instance.localName} in a render callback render()/component(Will|Did|First)Render(). This is not supported and will lead to an error in 4.0.0`);
    }

    const classProperty = getClassProperties(this).get(propertyKey);

    if ((classProperty.comparator || defaultComparator)(oldValue, newValue)) {
      // WILL BE REMOVED IN 4.0.0 -- start
      const interceptor = getClassPropertyInterceptor(this, propertyKey);
      newValue = interceptor.reduce((value, func) => {
        return func.apply(instance, [oldValue, value]) || value;
      }, newValue); // WILL BE REMOVED IN 4.0.0 -- end

      this._reflectAttributes(classProperty, instance, newValue, propertyKey.toString());

      instance[symbolPropertyState] = PROPERTY_STATE.UPDATE_PROPERTY;
      instance[propertyKey] = newValue;
      instance[symbolComponentState] = instance[symbolComponentState] | 0b0001;
      instance[symbolPropertyState] = PROPERTY_STATE.UPDATED; // WILL BE REMOVED IN 4.0.0

      const watcher = getClassPropertyWatcher(this, propertyKey);
      watcher.forEach(value => value.apply(instance, [oldValue, newValue])); // WILL BE REMOVED IN 4.0.0 -- end

      if (instance.componentShouldUpdate(propertyKey, oldValue, newValue)) {
        instance.scheduleRender();
      }
    }

    instance._updatingProperty = null;
  }

  static _reflectAttributes(classProperty, instance, newValue, propertyKey) {
    if ((classProperty.reflectAsAttribute || classProperty.reflectAsAttribute === undefined) && !(instance[symbolComponentState] & 0b00010)) {
      if (classProperty.type === Boolean || classProperty.type === String || classProperty.type === Number || classProperty.reflectAsAttribute === true) {
        if (instance[symbolPropertyState] !== PROPERTY_STATE.UPDATE_FROM_ATTRIBUTE) {
          instance[symbolPropertyState] = PROPERTY_STATE.REFLECTING;

          if (newValue === false || newValue === null || newValue === undefined) {
            instance.removeAttribute(camelToKebapCase(propertyKey));
          } else {
            if (classProperty.converter && classProperty.converter.toAttribute) {
              instance.setAttribute(camelToKebapCase(propertyKey), classProperty.converter.toAttribute(newValue, classProperty.type));
            } else {
              instance.setAttribute(camelToKebapCase(propertyKey), serializeValue(newValue, classProperty.type));
            }
          }
        } else if (classProperty.type === Boolean && (newValue === false || newValue === null || newValue === undefined)) {
          const state = instance[symbolPropertyState];
          instance[symbolPropertyState] = PROPERTY_STATE.REFLECTING;
          instance.removeAttribute(camelToKebapCase(propertyKey));
          instance[symbolPropertyState] = state;
        }
      }
    }
  }

  static get observedAttributes() {
    // filter out states -> type === undefined
    return Array.from(getClassProperties(this)).filter(value => value[1].type !== undefined).map(value => camelToKebapCase(value[0].toString()));
  }

  get _componentState() {
    // compatibility interop
    warn('WARNING! you access a protected property _componentState that will not be available anymore in 4.0.0, please use the lifecycle methods componentConnected/componentDisconnected'); // eslint-disable-line max-len

    if (this[symbolComponentState] & 0b0010) {
      return COMPONENT_STATE.INIT;
    } else if (this[symbolComponentState] & 0b0100) {
      return COMPONENT_STATE.CONNECTED;
    } else {
      return COMPONENT_STATE.DISCONNECTED;
    }
  }

  get _propertyState() {
    // compatibility interop
    warn('WARNING! you access a protected property _propertyState that will not be available anymore in 4.0.0');
    return this[symbolPropertyState];
  }

  set _propertyState(value) {
    // compatibility interop dashboard-elements 1.2.0 manually set property state
    warn('WARNING! you access a protected property _propertyState that will not be available anymore in 4.0.0');

    switch (value) {
      case PROPERTY_STATE.DIRTY:
        this[symbolComponentState] |= 0b0001;
        break;

      default:
        this[symbolPropertyState] = value;
    }
  }

  constructor() {
    super();

    _defineProperty(this, "_renderStrategy", RENDER_STRATEGY.DEFAULT);

    _defineProperty(this, symbolComponentState, 0b0011);

    _defineProperty(this, symbolPropertyState, PROPERTY_STATE.INIT);

    _defineProperty(this, "_updatingProperty", null);

    _defineProperty(this, "_templateCache", null);

    _defineProperty(this, "_firstRender", true);

    _defineProperty(this, "_renderScheduled", false);

    _defineProperty(this, "_renderScheduleId", null);

    _defineProperty(this, "_renderCompletedCallbacks", []);

    _defineProperty(this, "_layoutRAFReference", null);
  }
  /**
   * should return the DOM to be rendered
   */


  render() {} // eslint-disable-line

  /**
   * render a fallback if dependencies are not yet loaded
   */


  renderFallback() {
    return null;
  }
  /**
   * is called when the element is attached to the DOM
   */


  componentConnected() {} // eslint-disable-line

  /**
   * is called when the element is dettached from the DOM
   */


  componentDisconnected() {} // eslint-disable-line

  /**
   * is called just before render() will be exexuted
   */


  componentWillRender() {} // eslint-disable-line

  /**
   * is called just after render() will be exexuted
   */


  componentDidRender() {} // eslint-disable-line

  /**
   * is called just after the first render()
   */


  componentFirstRender() {} // eslint-disable-line

  /**
   * is called after render and broser layouting
   */


  componentDidLayout() {} // eslint-disable-line

  /**
   * determines if the changing of a property should lead to a new render
   *
   * @param _property - property name
   * @param _oldValue - old value
   * @param _newValue - new value
   */


  componentShouldUpdate(_property, _oldValue, _newValue) {
    return true;
  }
  /**
   * Triggered when a property changes
   *
   * @param _property - property which changed
   * @param _oldValue - old value
   * @param _newValue - new value
   */


  propertyChanged(_property, _oldValue, _newValue) {} // es-lint-disable-line @typescript-eslint/no-empty-function

  /**
   * return element whre the DOM from render will be rendered to
   */


  renderToElement() {
    if (!this.shadowRoot) {
      this.attachShadow({
        mode: 'open'
      });
    }

    return this.shadowRoot;
  }
  /**
   * return a Promise which will be resolved after construction of the element
   *
   * @returns Promise<void> promise which will resolve after construction is complete
   */


  async waitForConstruction() {
    return PolyfillPromise.resolve({});
  }
  /**
   * return a Promise which will be resolved after a successfull render
   *
   * @returns Promise<void>
   */


  async waitForRender() {
    return new PolyfillPromise(resolve => {
      this._renderCompletedCallbacks.push(resolve);
    });
  }
  /**
   * Schedule a new render (the render will only be scheduled) if
   * the componentstate is CONNECTED and propertystate is DIRTY
   * force will force a re-render
   *
   * @param force - force the re-render
   */


  scheduleRender(force = false) {
    if (this[symbolComponentState] & 0b1000 && !this._firstRender) {
      // allow scheduleRender call in first render
      warn(`WARNING: you called scheduleRender in a render callback render()/component(Will|Did|First)Render() of element ${this.localName}, this is not supported because it can lead to infinit render loops this will lead to an error in 4.0.0`);
      return;
    }

    if (this._firstRender && (force || this[symbolComponentState] & 0b0100)) {
      const options = getComponentProperties(this.constructor);

      if (!options.dependenciesLoaded && options.dependencies && options.dependencies.length > 0) {
        if (options.dependenciesLoading) {
          return;
        } // early exit


        const dependencies = (options.dependencies || []).filter(value => !HTMLElement.isPrototypeOf(value)).map(value => value()); // eslint-disable-line

        if (dependencies.length > 0) {
          // render fallback only if dependencies need to be laoded
          options.dependenciesLoading = true;
          PolyfillPromise.all(dependencies).then(result => {
            options.dependenciesLoaded = true;
            options.dependenciesLoading = false;
            result.forEach(value => {
              if (!HTMLElement.isPrototypeOf(value)) {
                console.log(`WARNING: dynamic dependency of element ${this.constructor.name} (${this.localName}) does not return a custom element`);
              }
            }); // update dependencies with resolved dependencies to skip dynamic resolving on the next element instatiation

            options.dependencies = [...options.dependencies.filter(value => HTMLElement.isPrototypeOf(value)), ...result];
            this.scheduleRender(force);
          });
          renderComponent.apply(this, [this.renderFallback()]);
          return; // early return
        }

        options.dependenciesLoaded = true;
      } else {
        options.dependenciesLoaded = true;
      }
    }

    if (force) {
      if (this._renderScheduled && this._renderScheduleId >= 0) {
        switch (this._renderStrategy) {
          case RENDER_STRATEGY.LAZY:
          case RENDER_STRATEGY.PIPELINE_EXPERIMENTAL:
            window.clearTimeout(this._renderScheduleId);
            break;

          case RENDER_STRATEGY.IMMEDIATE:
            clearImmediate(this._renderScheduleId);
            break;

          default:
            break;
        }

        this._renderScheduleId = null; // reset timeout/immediate render

        this._renderScheduled = false; // reset renderScheduled
      }

      if (!this._renderScheduled) {
        this._renderScheduled = true;
        PolyfillPromise.resolve().then(() => {
          renderComponent.apply(this);
          this._renderScheduled = false;
        });
      }
    } else if ((this[symbolComponentState] & 0b0101) === 0b0101 && !this._renderScheduled) {
      this._renderScheduled = true;
      let resolver = null;
      new PolyfillPromise(resolve => {
        resolver = resolve;
      }).then(() => {
        renderComponent.apply(this);
        this._renderScheduled = false;
        this._renderScheduleId = null;
      });

      switch (this._renderStrategy) {
        case RENDER_STRATEGY.LAZY:
        case RENDER_STRATEGY.PIPELINE_EXPERIMENTAL:
          this._renderScheduleId = window.setTimeout(resolver);
          break;

        case RENDER_STRATEGY.IMMEDIATE:
          this._renderScheduleId = requestImmediate(resolver);
          break;

        default:
          PolyfillPromise.resolve().then(resolver);
          break;
      }
    }
  }
  /**
   * build-in function please do not override
   */


  connectedCallback() {
    if (this[symbolComponentState] & 0b0010) {
      // on first connected reflect attributes
      this[symbolComponentState] = this[symbolComponentState] & 0b1101;
      this[symbolPropertyState] = PROPERTY_STATE.REFLECTING;
      const properties = getClassProperties(this.constructor);
      properties.forEach((value, key) => {
        const propValue = this[key.toString()];

        if (propValue || propValue === 0) {
          this.constructor._reflectAttributes(value, this, propValue, key.toString());
        }
      });
      this[symbolPropertyState] = PROPERTY_STATE.UPDATED;
      this[symbolComponentState] |= 0b0100;
      this.componentConnected();

      if (this._renderStrategy === RENDER_STRATEGY.DEFAULT) {
        // COMPATIBILITY interop: compatible to 3.1 render
        this.scheduleRender(true);
      } else {
        this.scheduleRender();
      }
    } else {
      this[symbolComponentState] |= 0b0100;
      this.componentConnected();
      this.scheduleRender();
    }
  }
  /**
   * build-in function please do not override
   */


  disconnectedCallback() {
    this[symbolComponentState] &= 0b1011;
    this.componentDisconnected();
  }
  /**
   * build-in function please do not override
   *
   * @param name - name of attribute
   * @param oldValue - old value
   * @param newValue - new value
   */


  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.constructor._fromAttribute(name, oldValue, newValue, this);
    }
  }

}
/**
 * shorthand for lazy rendered custom element
 */

export class LazyCustomElement extends CustomElement {
  constructor() {
    super();
    this._renderStrategy = RENDER_STRATEGY.LAZY;
  }

}
/** helper for shimmed browsers */

(function () {
  if ('ShadyCSS' in window && typeof window.ShadyCSS.ScopingShim.prepareAdoptedCssText === 'undefined') {
    console.error('Please check your "@webcomponents/webcomponentsjs" polyfill, minimum version 2.2.6 required');
  }
})();
